import { gridDateComparator, gridNumberComparator, GridRowId } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { Operator } from '@procureai/query-builder';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PriceSourceCell } from '../../cells/PriceSourceCell/PriceSourceCell';
import { CoverageAssistantGridField, CoveragePredictionRow } from '../../CoverageAssistantWidget.types';
import { SupplierFilterField } from '@/generated/gql/graphql';
import { BaseDomainType, I18nNamespace } from '~/common/types';
import { CuriosityListCountCellMenu } from '~/components/CuriosityListCountCellMenu/CuriosityListCountCellMenu';
import {
  cellFactory,
  DataGridConfig,
  DateRangeCell,
  DateRangeCellValue,
  editCellFactory,
  GridAlignmentType,
  gridColDefFactory,
  ListCountCell,
} from '~/components/DataGridCustom';
import { CommodityLinkWithDescriptionCell } from '~/components/DataGridCustom/cells/CommodityLinkWithDescriptionCell/CommodityLinkWithDescriptionCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { QuantityCell } from '~/components/DataGridCustom/cells/QuantityCell/QuantityCell';
import { RouteId } from '~/routes';
import { notEmpty } from '~/util/array';

export interface UseCoverageAssistantGridConfigProps {
  getRowIndex: (id: GridRowId) => number;
}

export const useCoverageAssistantGridConfig = ({ getRowIndex }: UseCoverageAssistantGridConfigProps) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CoverageAssistantGrid.column' });

  return useMemo<DataGridConfig<CoveragePredictionRow>>(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false, filterable: false } });

    return {
      columnVisibilityModel: { [CoverageAssistantGridField.UNIT]: false },
      initialState: {
        pinnedColumns: {
          right: [CoverageAssistantGridField.BASELINE_PRICE_PER_PRICE_UNIT],
        },
      },
      columns: [
        createColumn({
          field: CoverageAssistantGridField.MATERIAL,
          minWidth: 250,
          display: 'text',
          renderCell: cellFactory(CommodityLinkWithDescriptionCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            needs: {
              permission: Permission.BaseCommodityList,
            },
            description: row.title,
          })),
        }),
        createColumn({
          field: CoverageAssistantGridField.UNDERCOVERED_QUANTITY,
          minWidth: 180,
          align: GridAlignmentType.RIGHT,
          sortComparator: gridNumberComparator,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.undercoveredQuantity, unitId: row.unitId })),
        }),
        createColumn({
          field: CoverageAssistantGridField.PRICE_UNIT,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          sortComparator: gridNumberComparator,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.priceUnit, unitId: row.unitId })),
        }),
        createColumn({
          field: CoverageAssistantGridField.UNIT,
          flex: 1.5,
          renderCell: cellFactory(CuriosityValueCell, ({ row }) => ({
            value: row.unitId,
            ns: CuriosityNamespaceType.UNIT,
          })),
        }),
        createColumn({
          field: CoverageAssistantGridField.TIMEFRAME,
          minWidth: 200,
          renderCell: cellFactory(DateRangeCell),
          valueGetter: (_value, row): DateRangeCellValue => ({
            from: row.validFrom,
            to: row.validTo,
          }),
          sortComparator: (v1, v2, cellParams1, cellParams2) => {
            return gridDateComparator(v1?.to, v2?.to, cellParams1, cellParams2);
          },
        }),
        createColumn({
          field: CoverageAssistantGridField.ORGANIZATION,
          minWidth: 250,
          renderCell: cellFactory(CuriosityValueCell, ({ row }) => ({
            value: row.organizationId,
            ns: CuriosityNamespaceType.ORGANIZATION,
          })),
        }),
        createColumn({
          pinnable: true,
          field: CoverageAssistantGridField.BASELINE_PRICE_PER_PRICE_UNIT,
          minWidth: 250,
          renderCell: editCellFactory(PriceSourceCell, { getRowIndex }),
        }),
        createColumn({
          field: CoverageAssistantGridField.QUALIFIED_SUPPLIERS,
          minWidth: 300,
          renderCell: cellFactory(ListCountCell, ({ row }) => ({
            value: row.qualifiedSupplierIds.length,
            renderList:
              notEmpty(row.qualifiedSupplierIds) &&
              (() => (
                <CuriosityListCountCellMenu
                  ns={CuriosityNamespaceType.SUPPLIER}
                  list={row.qualifiedSupplierIds.map((id) => ({ id }))}
                  link={{
                    needs: { permission: Permission.BaseSupplierList },
                    all: {
                      label: t('widgets:CoverageAssistantGrid.menu.qualifiedSuppliers.all'),
                      pathname: RouteId.analyticsBase(BaseDomainType.SUPPLIER),
                      rules: [
                        {
                          field: SupplierFilterField.id,
                          operator: Operator.INEQ,
                          value: row.qualifiedSupplierIds,
                        },
                      ],
                    },
                    to: (id) => RouteId.supplierDetail(id),
                    component: Link,
                  }}
                />
              )),
          })),
          sortable: false,
        }),
      ],
    };
  }, [getRowIndex, t, tColumn]);
};
